import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import Navbar from "./components/Navbar";
import { applyLanguage, supportedLanguages, defaultLanguage } from "./i18n";
import "./i18n";

// تحميل كل صفحة بشكل منفصل (Code Splitting) بدل ما كل الصفحات تتحمّل
// دفعة واحدة مع أول زيارة للموقع - بيقلل حجم الجافاسكريبت الابتدائي بشكل كبير
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Landing = lazy(() => import("./pages/Landing"));
const CountryLanding = lazy(() => import("./pages/CountryLanding"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const Home = lazy(() => import("./pages/Home"));
const Search = lazy(() => import("./pages/Search"));
const Profile = lazy(() => import("./pages/Profile"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const ChatRequests = lazy(() => import("./pages/ChatRequests"));
const Chat = lazy(() => import("./pages/Chat"));
const Settings = lazy(() => import("./pages/Settings"));
const Admin = lazy(() => import("./pages/Admin"));
const Notifications = lazy(() => import("./pages/Notifications"));

function Layout({ children }) {
  return (
    <div className="min-h-screen">
      <Navbar />
      <main>{children}</main>
    </div>
  );
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
    </div>
  );
}

/**
 * بيقرأ اللغة من الرابط (/en/..., /fr/...) ويطبّقها فعليًا (نظام الترجمة + اتجاه الصفحة RTL/LTR).
 * لو مفيش لغة في الرابط، يبقى العربي (الافتراضي) من غير أي بادئة في الرابط.
 * لو حد كتب لغة مش مدعومة (زي /xx/home)، بنرجّعه للعربي.
 */
function LanguageWrapper() {
  const { lang } = useParams();

  useEffect(() => {
    applyLanguage(lang && lang in supportedLanguages ? lang : defaultLanguage);
  }, [lang]);

  if (lang && !(lang in supportedLanguages)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

// قايمة الروابط بمسارات بدون "/" في الأول، عشان تشتغل صح سواء بدون بادئة لغة (عربي)
// أو تحت بادئة "/en" أو "/fr" (نفس القايمة بتتكرر مرتين بس، مش مكتوبة مرتين يدويًا)
function buildAppRoutes(user) {
  return (
    <>
      <Route index element={user ? <Navigate to="home" replace /> : <Landing />} />
      <Route path="country/:slug" element={<CountryLanding />} />
      <Route path="about" element={<AboutUs />} />
      <Route path="contact" element={<ContactUs />} />
      <Route path="terms" element={<TermsOfService />} />
      <Route path="blog" element={<BlogList />} />
      <Route path="blog/:slug" element={<BlogPost />} />
      <Route path="login" element={user ? <Navigate to="home" replace /> : <Login />} />
      <Route path="register" element={user ? <Navigate to="home" replace /> : <Register />} />

      <Route path="home" element={<ProtectedRoute><Layout><Home /></Layout></ProtectedRoute>} />
      <Route path="search" element={<ProtectedRoute><Layout><Search /></Layout></ProtectedRoute>} />
      <Route path="profile/:userId" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
      <Route path="edit-profile" element={<ProtectedRoute><Layout><EditProfile /></Layout></ProtectedRoute>} />
      <Route path="chat-requests" element={<ProtectedRoute><Layout><ChatRequests /></Layout></ProtectedRoute>} />
      <Route path="chat" element={<ProtectedRoute><Layout><Chat /></Layout></ProtectedRoute>} />
      <Route path="chat/:conversationId" element={<ProtectedRoute><Layout><Chat /></Layout></ProtectedRoute>} />
      <Route path="settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
      <Route path="notifications" element={<ProtectedRoute><Layout><Notifications /></Layout></ProtectedRoute>} />
      <Route path="admin" element={<AdminRoute><Layout><Admin /></Layout></AdminRoute>} />
      <Route path="admin/blog" element={<AdminRoute><Layout><AdminBlog /></Layout></AdminRoute>} />

      <Route path="*" element={<Navigate to={user ? "home" : "login"} replace />} />
    </>
  );
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* العربي: بدون أي بادئة في الرابط (qiranzawaj.com/home) */}
        <Route path="/" element={<LanguageWrapper />}>
          {buildAppRoutes(user)}
        </Route>
        {/* الإنجليزي/الفرنسي: برابط منفصل (qiranzawaj.com/en/home، qiranzawaj.com/fr/home) */}
        <Route path="/:lang" element={<LanguageWrapper />}>
          {buildAppRoutes(user)}
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
